import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Divider, Flex, Select, Typography } from "antd";
import React from "react";
import { NoteType } from "../../domain/notes.domain";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import { useFuseSearch } from "../../hooks/useFuseSearch";
import { type NudgeNoteTypeEntry, useTemplateOptions } from "../../hooks/useTemplateOptions";
import {
  setAITemplateModalState,
  setShowRestrictionModal,
  setTemplateOpenedFromNoteModal,
} from "../../slices/appStateSlice";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import { rememberTemplateChoice } from "../../utils/sessionTemplate";
import styles from "./createnotemodal.module.scss";
import { useSchedulePickContext } from "./useSchedulePick";

// Hardcoded: SOL-specific NoteType ids that were removed from the picker. Existing notes/LC may
// still reference these — coerce the Select's value to undefined so it renders the placeholder
// instead of showing the raw enum number.
const REMOVED_SOL_NOTE_TYPES = new Set<number>([
  NoteType.SOL_PSYCH_INTAKE,
  NoteType.PSYCHIATRIC_FOLLOW_UP,
  NoteType.THERAPY_INTAKE,
  NoteType.THERAPY_FOLLOW_UP_SOAP,
  NoteType.THERAPY_FOLLOW_UP_DAP,
]);

/** Returns undefined for removed SOL note types so the Select shows its placeholder. Falls back to
 * the original value on any unexpected error to avoid breaking the picker. */
const getDisplayedNoteType = (noteType: number | string | undefined): number | string | undefined => {
  try {
    const asNumber = Number(noteType);
    if (Number.isFinite(asNumber) && REMOVED_SOL_NOTE_TYPES.has(asNumber)) {
      return undefined;
    }
    return noteType;
  } catch (error) {
    console.error("Failed to resolve displayed note type:", error);
    return noteType;
  }
};

const NoteTypeSelector = ({ noteTypes }: { noteTypes: NudgeNoteTypeEntry[] }) => {
  const componentName = "createnotemodal";
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileView();
  const { recordingDetail } = useAppSelector((state) => state.recordings);
  const { templateOpenedFromNoteModal } = useAppSelector((state) => state.appState);
  const { isMobilePath } = useAppSelector((state) => state.appState);
  // The template today's appointment suggested, while it is still the one selected.
  const { templateSuggestion, markTemplateTouched } = useSchedulePickContext();
  const suggestionShown = templateSuggestion != null && recordingDetail?.note_type === templateSuggestion.note_type;
  // "Change" in the hint opens the picker the way a click on it would.
  const [open, setOpen] = React.useState(false);
  // Refetch the user's list when a template was just added from the drawer.
  const { groups: allNoteTypes } = useTemplateOptions({ noteTypes, refreshKey: templateOpenedFromNoteModal });
  const { search: fuseSearch } = useFuseSearch({
    data: allNoteTypes.flatMap((group) => group.options),
    // searchText, not label: admin-shared labels are JSX, which Fuse silently can't match.
    keys: ["searchText"],
  });

  function handleAddTemplate() {
    if (isMobile) {
      alert("This feature is available on Desktop");
    } else if (isMobilePath) {
      dispatch(setShowRestrictionModal(true));
    } else {
      dispatch(setAITemplateModalState(true));
      dispatch(setTemplateOpenedFromNoteModal(true));
    }
  }

  return (
    <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
      <Select
        value={getDisplayedNoteType(recordingDetail?.note_type ?? undefined)}
        listHeight={isMobile ? 160 : 256}
        open={open}
        onOpenChange={setOpen}
        onChange={(value, option: any) => {
          markTemplateTouched(value);
          rememberTemplateChoice(value, Boolean(option.is_template));
          dispatch(
            setRecordingDetail({
              ...recordingDetail,
              note_type: value,
              template_id: option?.id,
              is_template: option?.is_template,
              modality_type: value === NoteType.CONSULT_NOTE ? "write" : recordingDetail.modality_type, // Compare string with string representation of enum
            }),
          );
        }}
        showSearch
        placeholder="Select a document type"
        optionFilterProp="children"
        // Use Fuse.js for fuzzy search in dropdown
        filterOption={(input, option: any) => {
          // Use any for option
          if (!input) return true;
          const results = fuseSearch(input);
          return results.some((item: any) => item.value === option.value); // Use any for item
        }}
        dropdownRender={(menu) => (
          <div className="client">
            {menu}
            <Divider style={{ margin: "4px 0" }} />
            <Flex gap={10}>
              <Button type="text" icon={<PlusOutlined />} onClick={handleAddTemplate}>
                Add My Template
              </Button>
            </Flex>
          </div>
        )}
        className={`${styles[`${componentName}__modal-row-field`]}`}
        options={allNoteTypes}
      />
      {suggestionShown && (
        <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 4 }} data-testid="template-suggestion-hint">
          Suggested from today's appointment ({templateSuggestion.visit_type}) ·{" "}
          <Button
            type="link"
            size="small"
            style={{ fontSize: 12, padding: 0, height: "auto" }}
            onClick={() => setOpen(true)}
          >
            Change
          </Button>
        </Typography.Text>
      )}
    </Col>
  );
};

export default NoteTypeSelector;
